import React, { useCallback, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import ErrorPage403 from '@/app/routes/errorPage403';
import { TextField, Textarea } from '@/shared/components/TextField';
import { Checkbox } from '@/shared/components/Checkbox/Checkbox';
import Loader from '@/app/routes/Loader';
import CreateLayout from '@/shared/layout/createLayout';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import {
  CERTIFICATE_LABELS,
  CERTIFICATE_PREVIEW_LABELS,
} from '../../constants/labels';
import { useEditCertTemplate } from '../../hooks/useEditCertTemplate';
import { CertificatePreview } from '../viewCertificateTemplate/CertificatePreview';
import type { CertificateTemplateDetail } from '../../types';

const SectionDivider = () => <hr className="border-[#DCDDE0]" />;

const FieldRowShimmer = () => (
  <div className="border border-[#DCDDE0] rounded-lg px-4 py-2.5 flex items-center gap-3 animate-pulse">
    <div className="w-4.5 h-4.5 rounded-sm bg-[#ECEEEF] shrink-0" />
    <div className="h-4 bg-[#ECEEEF] rounded w-2/5" />
  </div>
);

const FormPageHeader: React.FC = () => (
  <p className="text-titleLarge text-neutral-10">
    {CERTIFICATE_LABELS.EDIT_TITLE}
  </p>
);

const PreviewPageHeader: React.FC = () => (
  <p className="text-titleLarge text-neutral-10">
    {CERTIFICATE_LABELS.PREVIEW_TITLE}
  </p>
);

interface EditCertificateTemplateProps {
  templateId: string;
}

const EditCertificateTemplate: React.FC<EditCertificateTemplateProps> = ({
  templateId,
}) => {
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  const {
    loading,
    loadError,
    notFound,
    template,
    formState,
    loadingFields,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    isDirty,
    validationAttemptCount,
    setCertTemplateName,
    setHeaderField,
    setFooterField,
    setSubtitle,
    setIncludeTechnicianDetail,
    handleToggleField,
    handleOpenPreview,
    handleClosePreview,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseErrorSnackbar,
  } = useEditCertTemplate(templateId);

  useEffect(() => {
    if (validationAttemptCount === 0) {
      return;
    }
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('[data-error-field="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [validationAttemptCount]);

  const handlePreviewSubmit = useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleOpenPreview();
    },
    [handleOpenPreview]
  );

  const handleSaveSubmit = useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      void handleSaveFromPreview();
    },
    [handleSaveFromPreview]
  );

  if (privilegesLoading) {
    return null;
  }

  if (!privileges.certificateTemplateEdit) {
    return <ErrorPage403 />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (loadError || !template) {
    return (
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4">
        <p className="text-sm text-[#E11B1B]">
          {loadError || CERTIFICATE_LABELS.VIEW_ERROR_GENERIC}
        </p>
      </div>
    );
  }

  // ─── Preview mode ────────────────────────────────────────────────────────────

  if (isPreviewOpen) {
    const previewTemplate: CertificateTemplateDetail = {
      ...template,
      certTemplateName: formState.certTemplateName,
      headerField: formState.headerField,
      footerField: formState.footerField,
      subtitle: formState.subtitle || null,
      includeTechnicianDetail: formState.includeTechnicianDetail,
      selectedFormFields: formState.availableFormFields
        .filter((f) => formState.selectedFormFieldIds.includes(f.id))
        .map((f) => ({ id: f.id, label: f.label, type: 'text' })),
    };

    return (
      <CreateLayout
        key="preview"
        isSubmitting={isSubmitting}
        handleSubmit={handleSaveSubmit}
        handleCancel={handleClosePreview}
        isCancelModalOpen={false}
        handleCloseCancelModal={() => {}}
        handleConfirmCancel={() => {}}
        isSnackbarOpen={isErrorSnackbarOpen}
        handleCloseSnackbar={handleCloseErrorSnackbar}
        snackbarError={snackbarError}
        cancelButtonLabel={CERTIFICATE_LABELS.BUTTON_BACK}
        submitButtonLabel={CERTIFICATE_LABELS.BUTTON_SAVE}
        submitButtonLoadingLabel={CERTIFICATE_LABELS.BUTTON_SAVING}
        submitButtonDisabled={!isDirty || isSubmitting}
        Header={PreviewPageHeader}
        headerClassName="mb-5 py-2"
      >
        {/* ── Row 1: Template Name + Template ID + Task Form Name ── */}
        <div className="grid grid-cols-3 gap-4">
          <div className="min-w-0">
            <TextField
              label={CERTIFICATE_LABELS.FIELD_TEMPLATE_NAME}
              value={formState.certTemplateName}
              disabled
              fullWidth
            />
          </div>

          <div className="min-w-0">
            <TextField
              label={CERTIFICATE_LABELS.FIELD_TEMPLATE_ID}
              value={template.certTemplateId}
              disabled
              fullWidth
            />
          </div>

          <div className="min-w-0">
            <TextField
              label={CERTIFICATE_LABELS.FIELD_FORM_SELECT}
              value={template.formTemplate?.name ?? ''}
              disabled
              fullWidth
            />
          </div>
        </div>

        {/* ── Row 2: Task Form ID (disabled) ── */}
        <div className="grid grid-cols-3 gap-4">
          <div className="min-w-0">
            <TextField
              label={CERTIFICATE_LABELS.FIELD_TASK_FORM_ID}
              value={template.formTemplate?.formTemplateId ?? ''}
              disabled
              fullWidth
            />
          </div>
        </div>

        <SectionDivider />

        <CertificatePreview
          template={previewTemplate}
          className="w-full bg-white"
        />
      </CreateLayout>
    );
  }

  // ─── Form mode ───────────────────────────────────────────────────────────────

  return (
    <CreateLayout
      key="edit"
      isSubmitting={false}
      handleSubmit={handlePreviewSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isErrorSnackbarOpen}
      handleCloseSnackbar={handleCloseErrorSnackbar}
      snackbarError={snackbarError}
      cancelButtonLabel={CERTIFICATE_LABELS.BUTTON_CANCEL}
      submitButtonLabel={CERTIFICATE_LABELS.BUTTON_PREVIEW}
      Header={FormPageHeader}
      headerClassName="mb-5 py-2"
    >
      {/* ── Row 1: Template Name + Template ID + Task Form Name ── */}
      <div className="grid grid-cols-3 gap-4">
        <div
          className="min-w-0"
          data-error-field={errors.certTemplateName ? 'true' : undefined}
        >
          <TextField
            label={CERTIFICATE_LABELS.FIELD_TEMPLATE_NAME}
            value={formState.certTemplateName}
            onChange={(e) => setCertTemplateName(e.target.value)}
            maxLength={200}
            fullWidth
            autoFocus
            autoComplete="off"
            error={Boolean(errors.certTemplateName)}
            errorMessage={errors.certTemplateName ?? ''}
          />
        </div>

        <div className="min-w-0">
          <TextField
            label={CERTIFICATE_LABELS.FIELD_TEMPLATE_ID}
            value={template.certTemplateId}
            disabled
            fullWidth
          />
        </div>

        <div className="min-w-0">
          <TextField
            label={CERTIFICATE_LABELS.FIELD_FORM_SELECT}
            value={template.formTemplate?.name ?? ''}
            disabled
            fullWidth
          />
        </div>
      </div>

      {/* ── Row 2: Task Form ID (disabled) ── */}
      <div className="grid grid-cols-3 gap-4">
        <div className="min-w-0">
          <TextField
            label={CERTIFICATE_LABELS.FIELD_TASK_FORM_ID}
            value={template.formTemplate?.formTemplateId ?? ''}
            disabled
            fullWidth
          />
        </div>
      </div>

      <SectionDivider />

      {/* ── Certificate data section ── */}
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-[18px] font-normal leading-6.5 text-[#474A4C]">
            {CERTIFICATE_LABELS.SECTION_CERTIFICATE_DATA}
          </h2>
          <p className="text-sm leading-5 tracking-[0.25px] text-[#474A4C] mt-1">
            {CERTIFICATE_LABELS.SECTION_CERTIFICATE_DATA_DESC}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div
            className="min-w-0"
            data-error-field={errors.headerField ? 'true' : undefined}
          >
            <TextField
              label={CERTIFICATE_LABELS.FIELD_HEADER}
              value={formState.headerField}
              onChange={(e) => setHeaderField(e.target.value)}
              maxLength={200}
              fullWidth
              error={Boolean(errors.headerField)}
              errorMessage={errors.headerField ?? ''}
            />
          </div>

          <div
            className="min-w-0"
            data-error-field={errors.footerField ? 'true' : undefined}
          >
            <TextField
              label={CERTIFICATE_LABELS.FIELD_FOOTER}
              value={formState.footerField}
              onChange={(e) => setFooterField(e.target.value)}
              maxLength={200}
              fullWidth
              error={Boolean(errors.footerField)}
              errorMessage={errors.footerField ?? ''}
            />
          </div>
        </div>

        <div data-error-field={errors.subtitle ? 'true' : undefined}>
          <Textarea
            label={CERTIFICATE_LABELS.FIELD_SUBTITLE}
            value={formState.subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            maxLength={500}
            fullWidth
            rows={3}
            error={Boolean(errors.subtitle)}
            errorMessage={errors.subtitle ?? ''}
          />
        </div>
      </div>

      {/* ── Auto-data checkboxes ── */}
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-5 tracking-[0.25px] text-[#474A4C]">
          {CERTIFICATE_LABELS.AUTO_DATA_SECTION}
        </p>
        <div className="flex flex-col gap-1">
          <Checkbox
            checked
            disabled
            labelClassName="font-normal"
            label={CERTIFICATE_LABELS.CHECK_CLIENT_FACILITY}
          />
          <Checkbox
            checked
            disabled
            labelClassName="font-normal"
            label={CERTIFICATE_LABELS.CHECK_SERVICE_REQUEST}
          />
          <Checkbox
            checked={formState.includeTechnicianDetail}
            onChange={(checked) => setIncludeTechnicianDetail(checked)}
            labelClassName="font-normal"
            label={CERTIFICATE_LABELS.CHECK_TECHNICIAN}
          />
        </div>
      </div>

      <SectionDivider />

      {/* ── Form fields section ── */}
      <div className="flex flex-col gap-4">
        <div
          data-error-field={errors.selectedFormFieldIds ? 'true' : undefined}
        >
          <p className="text-sm font-normal leading-5 tracking-[0.25px] text-[#474A4C] wrap-break-word">
            {CERTIFICATE_LABELS.SECTION_FORM_FIELDS_DESC}
          </p>
          {errors.selectedFormFieldIds && (
            <p className="text-xs text-[#e11b1b] mt-1">
              {errors.selectedFormFieldIds}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {loadingFields ? (
            <>
              <FieldRowShimmer />
              <FieldRowShimmer />
              <FieldRowShimmer />
            </>
          ) : formState.availableFormFields.length === 0 ? (
            <p className="text-sm text-[#9B9EA0]">
              {CERTIFICATE_LABELS.FIELD_SELECTION_NO_FIELDS}
            </p>
          ) : (
            formState.availableFormFields.map((field) => (
              <div
                key={field.id}
                className="border border-[#DCDDE0] rounded-lg overflow-clip px-4 py-2.5 cursor-pointer has-focus-visible:ring-2 has-focus-visible:ring-[#181A1B] has-focus-visible:ring-offset-1 [&_label:has(:focus-visible)]:[box-shadow:none]!"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    handleToggleField(field.id);
                  }
                }}
              >
                <Checkbox
                  checked={formState.selectedFormFieldIds.includes(field.id)}
                  onChange={() => handleToggleField(field.id)}
                  labelClassName="font-normal"
                  label={field.label}
                />
              </div>
            ))
          )}

          {/* Mandatory fields — always present, cannot be deselected */}
          <div className="border border-[#DCDDE0] rounded-lg overflow-clip px-4 py-2.5">
            <Checkbox
              checked
              disabled
              labelClassName="font-normal"
              label={CERTIFICATE_PREVIEW_LABELS.TECHNICIAN_SIGNATURE}
            />
          </div>
          <div className="border border-[#DCDDE0] rounded-lg overflow-clip px-4 py-2.5">
            <Checkbox
              checked
              disabled
              labelClassName="font-normal"
              label={CERTIFICATE_PREVIEW_LABELS.CLIENT_SIGNATURE}
            />
          </div>
        </div>
      </div>
    </CreateLayout>
  );
};

export default EditCertificateTemplate;
